import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Camera, Loader2, PackagePlus, Upload } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { CameraCaptureModal } from "@/components/ui/CameraCaptureModal";
import { FLOOR_TEXT_INPUT_CLASS } from "@/components/ui/FreeTextSuggestionInput";
import { HubTouchSelect } from "@/components/ui/HubTouchSelect";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Input, Label } from "@/components/ui/Input";
import { SearchableMultiSelect } from "@/components/ui/SearchableMultiSelect";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";

import {
  fetchInboundArrivalFieldOptions,
  INBOUND_ARRIVALS_QUERY_KEY,
  logInboundArrival,
  printInboundArrivalPackageLabels,
  scanInboundArrivalLabel,
  type InboundArrivalLabelScanResult,
  type LogInboundArrivalPrefill,
} from "./inboundApi";
import {
  DEFAULT_INBOUND_ARRIVAL_UNIT_KIND,
  INBOUND_ARRIVAL_UNIT_KIND_OPTIONS,
  type InboundArrivalUnitKind,
} from "./inboundArrivalUnitKind";
import {
  INBOUND_ARRIVAL_LABEL_SCAN_JPEG_QUALITY,
  INBOUND_ARRIVAL_LABEL_SCAN_MAX_LONG_EDGE,
  prepareInboundArrivalLabelPhoto,
} from "./inboundArrivalLabelPhoto";
import {
  InboundArrivalVendorField,
  INBOUND_ARRIVAL_VENDOR_NOT_ON_LIST_LABEL,
  inboundArrivalVendorSubmitValue,
  type InboundArrivalVendorMode,
} from "./InboundArrivalVendorField";
import {
  inboundArrivalCarrierFromSelectValue,
  inboundArrivalCarrierSelectOptions,
  inboundArrivalIsHandDeliveredCarrier,
  inboundArrivalNotesWithRmaLink,
  inboundArrivalTrackingPlaceholder,
  logInboundArrivalCanSubmit,
  logInboundArrivalSubmitBlockers,
} from "./logInboundArrivalForm";
import {
  inboundReceiveRouteDockHubSelectOptions,
  inboundReceiveRouteRequiresPurchaseOrderLink,
  inboundReceiveRouteRequiresRmaLink,
  inboundReceiveRouteRequiresTransferLink,
  type InboundReceiveRoute,
} from "./inboundReceiveRoute";
import { runInboundArrivalLogAndPrint } from "./logInboundArrivalSubmit";

const MODAL_HELP =
  "Snap the freight POD, a BOL, or a UPS/FedEx label to fill vendor, boxes, carrier, and tracking, then log and print one arrived tag per carton. "
  + "Choose a receive route and link the purchase order, transfer, or RMA when that route requires it. "
  + "If tags fail to print, the arrival stays on the list; use Reprint there.";

function normalizePoNumbers(values: Iterable<string>): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (trimmed === "") continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    ordered.push(trimmed);
  }
  return ordered;
}

export function LogInboundArrivalModal({
  open,
  onClose,
  onLogged,
  selectedPrinter,
  prefill,
}: {
  open: boolean;
  onClose: () => void;
  onLogged: (printError: string | null) => void;
  selectedPrinter: string;
  prefill?: LogInboundArrivalPrefill;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const [vendorMode, setVendorMode] = useState<InboundArrivalVendorMode>("list");
  const [vendorListValue, setVendorListValue] = useState("");
  const [vendorCustomValue, setVendorCustomValue] = useState("");
  const [poNumbers, setPoNumbers] = useState<string[]>([]);
  const [poSearch, setPoSearch] = useState("");
  const [manualPoOpen, setManualPoOpen] = useState(false);
  const [manualPoDraft, setManualPoDraft] = useState("");
  const [boxCount, setBoxCount] = useState("1");
  const [unitKind, setUnitKind] = useState<InboundArrivalUnitKind>(DEFAULT_INBOUND_ARRIVAL_UNIT_KIND);
  const [receiveRoute, setReceiveRoute] = useState<InboundReceiveRoute | "">("");
  const [rmaNumber, setRmaNumber] = useState("");
  const [transferNumber, setTransferNumber] = useState("");
  const [transferSearch, setTransferSearch] = useState("");
  const [manualTransferOpen, setManualTransferOpen] = useState(false);
  const [manualTransferDraft, setManualTransferDraft] = useState("");
  const [carrier, setCarrier] = useState("");
  const [carrierSearch, setCarrierSearch] = useState("");
  const [carrierMode, setCarrierMode] = useState<"list" | "custom">("list");
  const [carrierCustom, setCarrierCustom] = useState("");
  const [tracking, setTracking] = useState("");
  const [noBolOrLabel, setNoBolOrLabel] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const submitLockRef = useRef(false);

  const fieldOptionsQuery = useQuery({
    queryKey: ["shipping", "inbound", "arrival-field-options"],
    queryFn: fetchInboundArrivalFieldOptions,
    staleTime: 5 * 60_000,
    enabled: open,
  });
  const fieldOptions = fieldOptionsQuery.data;
  const poOptions = useMemo(
    () =>
      (fieldOptions?.purchase_orders ?? []).map((poNumber) => ({
        value: poNumber,
        label: poNumber,
      })),
    [fieldOptions?.purchase_orders],
  );
  const transferOptions = useMemo(
    () =>
      (fieldOptions?.transfer_orders ?? []).map((transferOrderNumber) => ({
        value: transferOrderNumber,
        label: transferOrderNumber,
      })),
    [fieldOptions?.transfer_orders],
  );
  const carrierOptions = useMemo(
    () => inboundArrivalCarrierSelectOptions(fieldOptions?.carriers ?? [], carrierSearch),
    [fieldOptions?.carriers, carrierSearch],
  );
  const receiveRouteOptions = useMemo(() => inboundReceiveRouteDockHubSelectOptions(), []);
  const selectedPoValues = useMemo(() => new Set(poNumbers), [poNumbers]);

  function applyReceiveRoute(next: InboundReceiveRoute | ""): void {
    setReceiveRoute(next);
    if (next === "" || !inboundReceiveRouteRequiresPurchaseOrderLink(next)) {
      setPoNumbers([]);
      setPoSearch("");
      setManualPoOpen(false);
      setManualPoDraft("");
    }
    if (next === "" || !inboundReceiveRouteRequiresTransferLink(next)) {
      setTransferNumber("");
      setTransferSearch("");
      setManualTransferOpen(false);
      setManualTransferDraft("");
    }
    if (next === "" || !inboundReceiveRouteRequiresRmaLink(next)) {
      setRmaNumber("");
    }
  }

  function setSelectedPoValues(next: Set<string>): void {
    setPoNumbers(normalizePoNumbers(next));
  }

  function addManualPoNumber(): void {
    const trimmed = manualPoDraft.trim();
    if (trimmed === "") return;
    setPoNumbers((current) => normalizePoNumbers([...current, trimmed]));
    setManualPoDraft("");
    setPoSearch("");
    setManualPoOpen(false);
  }

  function resetForm(): void {
    setVendorMode("list");
    setVendorListValue("");
    setVendorCustomValue("");
    setPoNumbers([]);
    setPoSearch("");
    setManualPoOpen(false);
    setManualPoDraft("");
    setReceiveRoute("");
    setRmaNumber("");
    setTransferNumber("");
    setTransferSearch("");
    setManualTransferOpen(false);
    setManualTransferDraft("");
    setBoxCount("1");
    setUnitKind(DEFAULT_INBOUND_ARRIVAL_UNIT_KIND);
    setCarrier("");
    setCarrierSearch("");
    setCarrierMode("list");
    setCarrierCustom("");
    setTracking("");
    setNoBolOrLabel(false);
    setError(null);
    setCameraOpen(false);
    submitLockRef.current = false;
  }

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }
    if (prefill?.vendor_name) {
      setVendorMode("list");
      setVendorListValue(prefill.vendor_name);
      setVendorCustomValue("");
    }
    if (prefill?.po_numbers != null && prefill.po_numbers.length > 0) {
      setPoNumbers(normalizePoNumbers(prefill.po_numbers));
      setReceiveRoute("purchase_order");
    } else if (prefill?.po_number) {
      setPoNumbers(normalizePoNumbers([prefill.po_number]));
      setReceiveRoute("purchase_order");
    }
    if (prefill?.tracking) setTracking(prefill.tracking);
  }, [open, prefill]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !cameraOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, cameraOpen]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: INBOUND_ARRIVALS_QUERY_KEY });
  };

  const vendorSubmitValue = inboundArrivalVendorSubmitValue({
    mode: vendorMode,
    listValue: vendorListValue,
    customValue: vendorCustomValue,
  });
  const carrierSubmitValue = (carrierMode === "custom" ? carrierCustom : carrier).trim();

  function beginAddCarrier(): void {
    const typed = carrierSearch.trim();
    setCarrierMode("custom");
    setCarrierCustom(typed !== "" ? typed : carrier);
    setCarrierSearch("");
  }

  const logMutation = useMutation({
    mutationFn: () =>
      runInboundArrivalLogAndPrint({
        printer: selectedPrinter,
        logArrival: async () => {
          const logged = await logInboundArrival({
            vendor_name: vendorSubmitValue,
            box_count: Number(boxCount.trim()),
            unit_kind: unitKind,
            no_bol_or_label: noBolOrLabel,
            carrier: noBolOrLabel ? "" : carrierSubmitValue,
            tracking: noBolOrLabel ? "" : tracking.trim(),
            po_numbers:
              receiveRoute !== "" && inboundReceiveRouteRequiresPurchaseOrderLink(receiveRoute)
                ? poNumbers
                : [],
            transfer_number:
              receiveRoute !== "" && inboundReceiveRouteRequiresTransferLink(receiveRoute)
                ? transferNumber.trim()
                : null,
            notes:
              receiveRoute !== "" && inboundReceiveRouteRequiresRmaLink(receiveRoute)
                ? inboundArrivalNotesWithRmaLink(null, rmaNumber)
                : null,
            linked_source_type: prefill?.linked_source_type ?? null,
            linked_source_id: prefill?.linked_source_id ?? null,
            receive_route: receiveRoute === "" ? null : receiveRoute,
          });
          return { id: logged.arrival.id, packageIds: logged.packageIds };
        },
        printLabels: printInboundArrivalPackageLabels,
      }),
    onSuccess: (result) => {
      resetForm();
      invalidate();
      onLogged(result.printError);
    },
    onError: (mutationError) =>
      setError(mutationError instanceof Error ? mutationError.message : String(mutationError)),
    onSettled: () => {
      submitLockRef.current = false;
    },
  });

  const applyLabelScan = (scanned: InboundArrivalLabelScanResult) => {
    if (scanned.vendor_name != null) {
      if (scanned.vendor_matched === true) {
        setVendorMode("list");
        setVendorListValue(scanned.vendor_name);
        setVendorCustomValue("");
      } else {
        setVendorMode("custom");
        setVendorCustomValue(scanned.vendor_name);
        setVendorListValue("");
      }
    }
    const suggestedRoute = scanned.suggested_receive_route;
    if (suggestedRoute === "purchase_order") {
      applyReceiveRoute("purchase_order");
      if (scanned.po_number != null) {
        setPoNumbers(normalizePoNumbers([scanned.po_number]));
      }
    } else if (suggestedRoute === "transfer_order") {
      applyReceiveRoute("transfer_order");
      if (scanned.transfer_number != null) {
        setTransferNumber(scanned.transfer_number);
        setTransferSearch("");
      }
    } else if (scanned.po_number != null) {
      applyReceiveRoute("purchase_order");
      setPoNumbers(normalizePoNumbers([scanned.po_number]));
    } else if (scanned.transfer_number != null) {
      applyReceiveRoute("transfer_order");
      setTransferNumber(scanned.transfer_number);
      setTransferSearch("");
    }
    if (scanned.box_count != null) setBoxCount(String(scanned.box_count));
    if (scanned.carrier != null) {
      setCarrierMode("list");
      setCarrierCustom("");
      setCarrier(scanned.carrier);
    }
    if (scanned.tracking != null) setTracking(scanned.tracking);
    if (scanned.carrier != null || scanned.tracking != null) {
      setNoBolOrLabel(false);
    }
    setError(null);
  };

  const scanMutation = useMutation({
    mutationFn: async (file: File) => {
      const prepared = await prepareInboundArrivalLabelPhoto(file);
      return scanInboundArrivalLabel(prepared);
    },
    onSuccess: applyLabelScan,
    onError: (mutationError) =>
      setError(mutationError instanceof Error ? mutationError.message : String(mutationError)),
  });

  function handleLabelPhoto(file: File | undefined): void {
    if (file == null) return;
    scanMutation.mutate(file);
  }

  const boxCountValid = /^\d+$/.test(boxCount.trim()) && Number(boxCount.trim()) >= 1;
  const formBusy = logMutation.isPending || scanMutation.isPending;
  const submitBlockers = logInboundArrivalSubmitBlockers({
    vendorName: vendorSubmitValue,
    boxCountValid,
    noBolOrLabel,
    carrier: carrierSubmitValue,
    tracking,
    receiveRoute,
    poNumbers,
    transferNumber,
    rmaNumber,
  });
  const canLog = logInboundArrivalCanSubmit({
    vendorName: vendorSubmitValue,
    boxCountValid,
    noBolOrLabel,
    carrier: carrierSubmitValue,
    tracking,
    receiveRoute,
    poNumbers,
    transferNumber,
    rmaNumber,
    formBusy,
  });

  if (!open) return null;

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!canLog || submitLockRef.current) return;
    submitLockRef.current = true;
    logMutation.mutate();
  };

  return createPortal(
    <>
      <div
        {...hubModalOverlayProps(false)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="log-inbound-arrival-title"
        aria-busy={formBusy || undefined}
      >
        <form
          className="my-4 w-full max-w-lg rounded-lg border border-slate-200 bg-white shadow-xl"
          onSubmit={handleSubmit}
        >
          <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
            <div className="flex items-center gap-2">
              <h2 id="log-inbound-arrival-title" className="text-lg font-semibold text-slate-900">
                New dock arrival
              </h2>
              <HoverHintWithGlyph hoverHint={MODAL_HELP} size="md" />
            </div>
          </div>

          <div className="space-y-4 px-4 py-4 sm:px-5">
            <div className="flex flex-wrap items-end gap-2">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                className="sr-only"
                tabIndex={-1}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  void handleLabelPhoto(file);
                }}
              />
              <Button
                type="button"
                className="h-12 gap-2"
                disabled={formBusy}
                isLoading={scanMutation.isPending}
                onClick={() => setCameraOpen(true)}
              >
                <Camera className="h-4 w-4" />
                Scan POD, BOL, or label
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="h-12 gap-2"
                disabled={formBusy}
                onClick={() => photoInputRef.current?.click()}
              >
                <Upload className="h-4 w-4" />
                Choose photo
              </Button>
              {scanMutation.isPending ? (
                <p className="flex items-center gap-2 text-sm text-slate-600" role="status">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Stay on this screen. Reading the photo…
                </p>
              ) : null}
            </div>
            <div className="space-y-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label htmlFor="arrival-vendor">Vendor</Label>
                {vendorMode === "list" ? (
                  <button
                    type="button"
                    className="text-sm font-medium text-brand-700 underline-offset-2 hover:underline disabled:text-slate-400"
                    disabled={scanMutation.isPending}
                    onClick={() => setVendorMode("custom")}
                  >
                    {INBOUND_ARRIVAL_VENDOR_NOT_ON_LIST_LABEL}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="text-sm font-medium text-brand-700 underline-offset-2 hover:underline disabled:text-slate-400"
                    disabled={scanMutation.isPending}
                    onClick={() => setVendorMode("list")}
                  >
                    Choose from supplier list
                  </button>
                )}
              </div>
              <InboundArrivalVendorField
                id="arrival-vendor"
                mode={vendorMode}
                listValue={vendorListValue}
                onListValueChange={setVendorListValue}
                customValue={vendorCustomValue}
                onCustomValueChange={setVendorCustomValue}
                suggestions={fieldOptions?.vendors ?? []}
                disabled={scanMutation.isPending}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="arrival-receive-route">Receive route</Label>
                <HubTouchSelect
                  id="arrival-receive-route"
                  size="touch"
                  className="w-full max-w-none"
                  value={receiveRoute}
                  onChange={(next) => applyReceiveRoute(next as InboundReceiveRoute | "")}
                  options={receiveRouteOptions}
                  disabled={scanMutation.isPending}
                />
              </div>
              {receiveRoute !== "" && inboundReceiveRouteRequiresPurchaseOrderLink(receiveRoute) ? (
                <div className="space-y-1 sm:col-span-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label htmlFor="arrival-po">Purchase order</Label>
                    {!manualPoOpen ? (
                      <button
                        type="button"
                        className="text-sm font-medium text-brand-700 underline-offset-2 hover:underline disabled:text-slate-400"
                        disabled={scanMutation.isPending}
                        onClick={() => setManualPoOpen(true)}
                      >
                        PO not on list
                      </button>
                    ) : null}
                  </div>
                  <SearchableMultiSelect
                    id="arrival-po"
                    variant="chips"
                    addLabel="Add PO"
                    selectedValues={selectedPoValues}
                    onSelectedValuesChange={setSelectedPoValues}
                    options={poOptions}
                    searchValue={poSearch}
                    onSearchChange={setPoSearch}
                    placeholder="Select purchase orders"
                    searchPlaceholder="Search GPO numbers…"
                    emptyMessage="No purchase orders match."
                    isLoading={fieldOptionsQuery.isLoading}
                    disabled={scanMutation.isPending}
                    panelMinWidth={280}
                  />
                  {manualPoOpen ? (
                    <div className="flex flex-wrap items-end gap-2 pt-1">
                      <div className="min-w-[10rem] flex-1 space-y-1">
                        <Label htmlFor="arrival-po-manual" className="text-xs text-slate-600">
                          Type a PO not listed
                        </Label>
                        <Input
                          id="arrival-po-manual"
                          value={manualPoDraft}
                          onChange={(event) => setManualPoDraft(event.target.value)}
                          placeholder="GPO-1201"
                          autoComplete="off"
                          autoCorrect="off"
                          spellCheck={false}
                          disabled={scanMutation.isPending}
                          className={cn(FLOOR_TEXT_INPUT_CLASS, "touch-manipulation")}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              addManualPoNumber();
                            }
                          }}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-12"
                        disabled={scanMutation.isPending || manualPoDraft.trim() === ""}
                        onClick={addManualPoNumber}
                      >
                        Add PO
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-12"
                        disabled={scanMutation.isPending}
                        onClick={() => {
                          setManualPoOpen(false);
                          setManualPoDraft("");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {receiveRoute !== "" && inboundReceiveRouteRequiresTransferLink(receiveRoute) ? (
                <div className="space-y-1 sm:col-span-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label htmlFor="arrival-transfer">Transfer order</Label>
                    {!manualTransferOpen ? (
                      <button
                        type="button"
                        className="text-sm font-medium text-brand-700 underline-offset-2 hover:underline disabled:text-slate-400"
                        disabled={scanMutation.isPending}
                        onClick={() => setManualTransferOpen(true)}
                      >
                        Transfer not on list
                      </button>
                    ) : null}
                  </div>
                  <SearchableSelect
                    id="arrival-transfer"
                    value={transferNumber}
                    onValueChange={(option) => {
                      setTransferNumber(option?.value ?? "");
                      setTransferSearch("");
                    }}
                    options={transferOptions}
                    searchValue={transferSearch}
                    onSearchChange={setTransferSearch}
                    placeholder="Select a transfer order"
                    searchPlaceholder="Search transfer numbers…"
                    emptyMessage="No transfer orders match."
                    selectedLabelOverride={transferNumber || null}
                    isLoading={fieldOptionsQuery.isLoading}
                    disabled={scanMutation.isPending || manualTransferOpen}
                    panelMinWidth={280}
                    triggerClassName="h-12 w-full max-w-none touch-manipulation"
                  />
                  {manualTransferOpen ? (
                    <div className="flex flex-wrap items-end gap-2 pt-1">
                      <div className="min-w-[10rem] flex-1 space-y-1">
                        <Label htmlFor="arrival-transfer-manual" className="text-xs text-slate-600">
                          Type a transfer not listed
                        </Label>
                        <Input
                          id="arrival-transfer-manual"
                          value={manualTransferDraft}
                          onChange={(event) => setManualTransferDraft(event.target.value)}
                          placeholder="TR-1201"
                          autoComplete="off"
                          autoCorrect="off"
                          spellCheck={false}
                          disabled={scanMutation.isPending}
                          className={cn(FLOOR_TEXT_INPUT_CLASS, "touch-manipulation")}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              const trimmed = manualTransferDraft.trim();
                              if (trimmed === "") return;
                              setTransferNumber(trimmed);
                              setManualTransferDraft("");
                              setTransferSearch("");
                              setManualTransferOpen(false);
                            }
                          }}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-12"
                        disabled={scanMutation.isPending || manualTransferDraft.trim() === ""}
                        onClick={() => {
                          const trimmed = manualTransferDraft.trim();
                          if (trimmed === "") return;
                          setTransferNumber(trimmed);
                          setManualTransferDraft("");
                          setTransferSearch("");
                          setManualTransferOpen(false);
                        }}
                      >
                        Use transfer
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-12"
                        disabled={scanMutation.isPending}
                        onClick={() => {
                          setManualTransferOpen(false);
                          setManualTransferDraft("");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {receiveRoute !== "" && inboundReceiveRouteRequiresRmaLink(receiveRoute) ? (
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="arrival-rma">RMA</Label>
                  <Input
                    id="arrival-rma"
                    value={rmaNumber}
                    onChange={(event) => setRmaNumber(event.target.value)}
                    placeholder="Customer RMA number"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    disabled={scanMutation.isPending}
                    className={cn(FLOOR_TEXT_INPUT_CLASS, "touch-manipulation")}
                  />
                </div>
              ) : null}
              <div className="space-y-1">
                <Label htmlFor="arrival-unit-kind">Unit type</Label>
                <HubTouchSelect
                  id="arrival-unit-kind"
                  size="touch"
                  className="w-full max-w-none"
                  value={unitKind}
                  onChange={(next) => setUnitKind(next as InboundArrivalUnitKind)}
                  options={INBOUND_ARRIVAL_UNIT_KIND_OPTIONS}
                  disabled={scanMutation.isPending}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="arrival-boxes">Unit count</Label>
                <Input
                  id="arrival-boxes"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  value={boxCount}
                  onChange={(event) => setBoxCount(event.target.value)}
                  disabled={scanMutation.isPending}
                  className={cn(FLOOR_TEXT_INPUT_CLASS, "touch-manipulation")}
                />
              </div>
              {!noBolOrLabel ? (
                <>
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Label htmlFor="arrival-carrier">Carrier</Label>
                      {carrierMode === "custom" ? (
                        <button
                          type="button"
                          className="text-sm font-medium text-brand-700 underline-offset-2 hover:underline disabled:text-slate-400"
                          disabled={scanMutation.isPending}
                          onClick={() => {
                            setCarrierMode("list");
                            setCarrier(carrierCustom.trim());
                            setCarrierCustom("");
                          }}
                        >
                          Choose from list
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="text-sm font-medium text-brand-700 underline-offset-2 hover:underline disabled:text-slate-400"
                          disabled={scanMutation.isPending}
                          onClick={beginAddCarrier}
                        >
                          Add carrier
                        </button>
                      )}
                    </div>
                    {carrierMode === "custom" ? (
                      <Input
                        id="arrival-carrier"
                        value={carrierCustom}
                        onChange={(event) => setCarrierCustom(event.target.value)}
                        placeholder="Carrier name"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        disabled={scanMutation.isPending}
                        className={cn(FLOOR_TEXT_INPUT_CLASS, "touch-manipulation")}
                      />
                    ) : (
                      <SearchableSelect
                        id="arrival-carrier"
                        value={carrier}
                        onValueChange={(option) => {
                          if (option == null) {
                            setCarrier("");
                            setCarrierSearch("");
                            return;
                          }
                          setCarrier(inboundArrivalCarrierFromSelectValue(option.value));
                          setCarrierSearch("");
                        }}
                        options={carrierOptions}
                        searchValue={carrierSearch}
                        onSearchChange={setCarrierSearch}
                        placeholder="Select carrier"
                        searchPlaceholder="Search carriers…"
                        emptyMessage="No carriers match."
                        selectedLabelOverride={carrier || null}
                        isLoading={fieldOptionsQuery.isLoading}
                        disabled={scanMutation.isPending}
                        panelMinWidth={240}
                        triggerClassName="h-12 w-full max-w-none touch-manipulation"
                        panelFooter={({ close }) => (
                          <Button
                            type="button"
                            variant="secondary"
                            className="h-12 w-full"
                            disabled={scanMutation.isPending}
                            onClick={() => {
                              close();
                              beginAddCarrier();
                            }}
                          >
                            Add carrier
                          </Button>
                        )}
                      />
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="arrival-tracking">Tracking</Label>
                    <Input
                      id="arrival-tracking"
                      value={tracking}
                      onChange={(event) => setTracking(event.target.value)}
                      placeholder={inboundArrivalTrackingPlaceholder(carrierSubmitValue)}
                      autoComplete="off"
                      autoCorrect={inboundArrivalIsHandDeliveredCarrier(carrierSubmitValue) ? "on" : "off"}
                      autoCapitalize={inboundArrivalIsHandDeliveredCarrier(carrierSubmitValue) ? "words" : "off"}
                      spellCheck={inboundArrivalIsHandDeliveredCarrier(carrierSubmitValue)}
                      disabled={scanMutation.isPending}
                      className={cn(FLOOR_TEXT_INPUT_CLASS, "touch-manipulation")}
                    />
                  </div>
                </>
              ) : null}
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-slate-300"
                checked={noBolOrLabel}
                disabled={scanMutation.isPending}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setNoBolOrLabel(checked);
                  if (checked) {
                    setCarrier("");
                    setCarrierSearch("");
                    setCarrierMode("list");
                    setCarrierCustom("");
                    setTracking("");
                  }
                }}
              />
              <span>
                No POD or label
                <span className="block text-xs text-slate-500">
                  Driver dropped freight without a signed POD or shipping label. Carrier and tracking are not required.
                </span>
              </span>
            </label>
            {error ? (
              <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {error}
              </p>
            ) : null}
          </div>

          <div className="border-t border-slate-200 px-4 py-3 sm:px-5">
            {!canLog && submitBlockers.length > 0 && !formBusy ? (
              <p className="mb-3 text-sm text-slate-600" role="status">
                {submitBlockers.join(" ")}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={onClose} disabled={formBusy}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canLog} isLoading={logMutation.isPending} className="gap-2">
                <PackagePlus className="h-4 w-4" />
                Log and print tags
              </Button>
            </div>
          </div>
        </form>
      </div>
      <CameraCaptureModal
        open={cameraOpen}
        title="POD, BOL, or shipping label"
        filenamePrefix="arrival-label"
        jpegQuality={INBOUND_ARRIVAL_LABEL_SCAN_JPEG_QUALITY}
        maxLongEdge={INBOUND_ARRIVAL_LABEL_SCAN_MAX_LONG_EDGE}
        onClose={() => setCameraOpen(false)}
        onCapture={(file) => {
          setCameraOpen(false);
          handleLabelPhoto(file);
        }}
      />
    </>,
    document.body,
  );
}
